import React, { useMemo, useRef } from "react";
import { Card as RBCard } from "react-bootstrap";
import customIndiaMap from "../../../../assets/map/custom-india-map.topo.json";
import Highcharts from "highcharts/highmaps";
import HighchartsReact from "highcharts-react-official";
import "../Admin.css";

const IndiaMap = ({ stateWiseData = [], selectedState, onStateSelect }) => {
  const chartRef = useRef(null);

  // Create a map of state_lgd to state data for quick lookup
  const stateDataMap = useMemo(() => {
    const map = {};
    stateWiseData.forEach((state) => {
      map[state?.state_lgd] = state;
    });
    return map;
  }, [stateWiseData]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        map: customIndiaMap,
        backgroundColor: "transparent",
        height: 550,
      },
      title: { text: "" },
      accessibility: { enabled: false },
      credits: { enabled: false },
      legend: { enabled: false },
      mapNavigation: { enabled: false },

      tooltip: {
        useHTML: true,
        backgroundColor: "#ffffff",
        borderWidth: 1,
        borderColor: "#D1D5DC",
        borderRadius: 8,
        shadow: true,
        style: {
          padding: "12px",
        },
        formatter: function () {
          const stateName = this.point.name;
          const stateLgd = this.point.properties?.state_lgd;
          const stateData = stateDataMap[stateLgd];

          if (!stateData) {
            return `<div style="font-weight: 600; margin-bottom: 4px;">${stateName}</div>
                  <div style="color: #666;">No data available</div>`;
          }

          return `
          <div style="min-width: 180px;">
            <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px; color: #1E293B; padding-bottom: 2px; border-bottom: 1px solid #E5E7EB;">
              ${stateName}
            </div>
            <div style="display: flex; flex-direction: column; gap: 4px; font-size: 12px;">
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #9096A2;">Total TCCC :</span>
                <span style="font-weight: 600; color: #1E293B;">${stateData?.tccc_facilities}</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #9096A2;">Total DCCC :</span>
                <span style="font-weight: 600; color: #1E293B;">${stateData?.dccc_facilities}</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #9096A2;">Total Registered Beneficiaries :</span>
                <span style="font-weight: 600; color: #1E293B;">${stateData?.total_patients}</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #9096A2;">No. of Beneficiaries Under Treatment at DCCC :</span>
                <span style="font-weight: 600; color: #1E293B;">${stateData?.total_dccc}</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #9096A2;">No. of Pending Referrals at TCCC :</span>
                <span style="font-weight: 600; color: #1E293B;">${stateData?.total_mi}</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #9096A2;">Total Completed Chemo :</span>
                <span style="font-weight: 600; color: #1E293B;">${stateData?.total_chemo}</span>
              </div>
            </div>
          </div>
        `;
        },
      },

      plotOptions: {
        series: {
          point: {
            events: {
              click: function () {
                const stateLgd = this.properties?.state_lgd;
                if (stateLgd && onStateSelect) {
                  onStateSelect(stateLgd);
                }
              },
            },
          },
        },
      },

      series: [
        {
          cursor: "pointer",
          type: "map",
          mapData: customIndiaMap,
          data: customIndiaMap.features.map((feature) => {
            const stateLgd = feature.properties.state_lgd;
            const isSelected =
              selectedState && selectedState?.state_lgd === stateLgd;

            return {
              "hc-key": feature.properties["hc-key"],
              name: feature.properties.name,
              properties: feature.properties, // Pass all properties including state_lgd
              color: isSelected ? "#6C7FF9" : "#C8CDFF",
            };
          }),
          borderColor: "#ffffff",
          borderWidth: 1,
          nullColor: "#E5E7EB",
          showInLegend: false,
          states: {
            hover: {
              enabled: true,
              color: "#6C7FF9",
              borderColor: "#ffffff",
              brightness: 0.1,
            },
            select: {
              color: "#6C7FF9",
              borderColor: "#ffffff",
            },
          },
          dataLabels: {
            enabled: true,
            format: "{point.properties.hc-a2}", // Show 2-letter state codes
            style: {
              fontSize: "12px",
              fontWeight: "600",
              color: "#000",
              textOutline: "none",
            },
          },
          enableMouseTracking: true,
          zIndex: 1,
        },
      ],
    }),
    [stateWiseData, selectedState, stateDataMap, onStateSelect]
  );

  return (
    <>
      <RBCard
        className="rounded-4 border-0 shadow-sm"
        style={{ height: "600px" }}
      >
        {" "}
        <div className="d-flex justify-content-between align-items-center w-100 p-2">
          <p className="state-title">State-Wise Distribution</p>
          <p className="state-subtitle">
            Click on any state to view detailed statistics
          </p>
        </div>
        <RBCard.Body className="p-0">
          <HighchartsReact
            ref={chartRef}
            highcharts={Highcharts}
            constructorType="mapChart"
            options={chartOptions}
            containerProps={{ style: { height: "100%", width: "100%" } }}
          />
          <p className="map-note text-center">
            <strong>Note:</strong> The map is for illustrative or artistic
            purposes and is not for official or legal use.
          </p>
        </RBCard.Body>

      </RBCard>
    </>
  );
};

export default IndiaMap;
